import React, { forwardRef, RefObject, useContext } from 'react';
import {
  MediumCard,
  CardValue,
  MeetingSeatRatioRow,
  CardRow,
  PaddedCardTitle,
} from 'spw-programming-projects/program-summary/single-page-summary/styles';
import { Grid, Box } from '@mui/material';
import { asRatio, numberWithCommasOrDash, valueOrZero } from 'spw-shared/utils';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import {
  CardSmallValue,
  CardValueTitle,
  Pill,
} from 'spw-programming-projects/program-summary/components/styles';
import { MEETING_SEAT_RATIO_VALUES } from 'spw-components/dialogs/total-program/utils';
import { useDialogStatus } from 'spw-components/dialogs/total-program/use-dialog-status';
import { MeetingSeatRatioDialog } from 'spw-components/dialogs/total-program/meeting-seat-ratio-dialog';
import { ProgramSummaryCardType } from 'spw-esa-projects/program-summary/icon-selector-popover';
import { useCardIcon } from 'spw-esa-projects/program-summary/cards/hooks';
import CardIcon from 'spw-esa-projects/program-summary/card-icon';
import { ProgrammingSummaryCardsContext } from '../program-summary-tabs/summary-card-context';
import { IconWrapper } from '../macro-single-page-summary/styles';
import { PlainWorksheet } from '../../../../models/Worksheet';

export interface IMeetingSeatRatioProps {
  futureWorkSeats: number;
  futureMeetingSeats: number;
  height?: string;
  isTotalProgramScenario: boolean;
  totalProgramWorksheets?: PlainWorksheet[];
}

export const MeetingSeatRatio = forwardRef<HTMLElement, IMeetingSeatRatioProps>(
  (
    {
      futureWorkSeats,
      futureMeetingSeats,
      height,
      isTotalProgramScenario,
      totalProgramWorksheets,
    },
    ref
  ) => {
    const meetingSeatsToWorkSeats = Number(
      valueOrZero(futureMeetingSeats / futureWorkSeats)
    );

    const cardIcon = useCardIcon(ProgramSummaryCardType.meetingSeatRatio);
    const { selectedCardIcon, onClickCardIcon } = useContext(
      ProgrammingSummaryCardsContext
    );

    const handleIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onClickCardIcon(event, cardIcon);
    };

    const isIconCardSelected =
      selectedCardIcon?.cardType === ProgramSummaryCardType.meetingSeatRatio;

    const {
      showDialog,
      openDialog,
      valueType,
      dialogValue,
      setShowDialog,
    } = useDialogStatus(isTotalProgramScenario);

    const meetingSeatsToWorkSeatsValue = meetingSeatsToWorkSeats
      ? asRatio(meetingSeatsToWorkSeats, 2, true)
      : NO_DATA_TO_SHOW;

    const showMeetingSeatsToWorkSeats = () => {
      openDialog(
        MEETING_SEAT_RATIO_VALUES.MEETING_SEATS_TO_WORK_SEATS,
        meetingSeatsToWorkSeats
      );
    };

    const showFutureMeetingSeats = () => {
      openDialog(
        MEETING_SEAT_RATIO_VALUES.FUTURE_MEETING_SEATS,
        futureMeetingSeats
      );
    };

    return (
      <>
        {showDialog && isTotalProgramScenario && !window.SPWExport && (
          <MeetingSeatRatioDialog
            totalProgramWorksheets={totalProgramWorksheets}
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            value={dialogValue}
            valueType={valueType}
            showMeetingSeatsToWorkSeats={showMeetingSeatsToWorkSeats}
            showFutureMeetingSeats={showFutureMeetingSeats}
            anchorRef={ref}
          />
        )}
        <div ref={ref as RefObject<HTMLDivElement>} style={{ height: '100%' }}>
          <MediumCard
            data-testid="meeting_seat_ratio"
            style={{ boxSizing: 'border-box', height: height || 130 }}
          >
            <PaddedCardTitle
              data-testid="programming_ps_meeting_seat_ratio_title"
              style={{ marginBottom: '32px' }}
            >
              MEETING SEAT RATIO
            </PaddedCardTitle>
            <Grid container style={{ paddingBottom: '20px' }}>
              <Grid item xs={12}>
                <MeetingSeatRatioRow display="flex">
                  <IconWrapper style={{ width: 46 }}>
                    <CardIcon
                      iconPath={`/images/space-icons/${cardIcon.iconPath}`}
                      onClick={handleIconClick}
                      height={42}
                      containerHeight={42}
                      width={48}
                      isSelected={isIconCardSelected}
                    />
                  </IconWrapper>
                  <Box
                    onClick={showMeetingSeatsToWorkSeats}
                    className={`
                  ${isTotalProgramScenario ? 'highlight' : ''}
                `}
                  >
                    <CardValue data-testid="programming_ps_meeting_seat_meetingSeatsToWorkSeats">
                      {meetingSeatsToWorkSeatsValue}
                    </CardValue>
                    <CardValueTitle>Meeting Seats to Work Seats</CardValueTitle>
                  </Box>
                </MeetingSeatRatioRow>
                <Box>
                  <CardRow>
                    <Box
                      display="flex"
                      style={{ alignItems: 'center', marginBottom: 4 }}
                    >
                      <Pill
                        style={{
                          paddingRight: 3,
                          width: 'auto',
                        }}
                        color="transparent"
                        className={`${
                          isTotalProgramScenario ? 'highlight' : ''
                        }`}
                        onClick={showFutureMeetingSeats}
                      >
                        <CardSmallValue
                          style={{ marginRight: 4, color: '#000000' }}
                          className="meeting-seats"
                          data-testid="programming_ps_meeting_seat_futureMeetingSeats"
                        >
                          {numberWithCommasOrDash(futureMeetingSeats)}
                        </CardSmallValue>
                        Meeting Seats
                      </Pill>
                      /
                    </Box>
                    <Pill color="transparent">
                      <CardSmallValue
                        style={{ marginRight: 4, color: '#000000' }}
                        className="work-seats"
                        data-testid="programming_ps_meeting_seat_workseats"
                      >
                        {numberWithCommasOrDash(futureWorkSeats)}
                      </CardSmallValue>
                      Work Seats
                    </Pill>
                  </CardRow>
                </Box>
              </Grid>
            </Grid>
          </MediumCard>
        </div>
      </>
    );
  }
);
